/**
 * CV Analysis Service
 * Handles CV competency analysis operations
 */
import axios from 'axios';
import { API_BASE_URL } from '../config';
import { createErrorLogger, ErrorType } from '../utils/logger';

const logger = createErrorLogger('CVAnalyzerService');

const CVAnalyzerService = {
  /**
   * Process a CV to extract competencies
   * @param {string} cvText
   * @param {File[]} files
   * @param {string} userId - The user ID to associate with this analysis
   * @param {string} language - Optional language for the CV (en/fr)
   * @returns {Promise<{ competencies: Array }>}
   */
  processCv: async (cvText, files = [], userId, language = 'en') => {
    logger.info('processCv', `processCv called for user: ${userId}`, { userId, language });
    const formData = new FormData();
    formData.append('cv_text', cvText || '');
    files.forEach(file => formData.append('files', file));
    formData.append('user_id', userId);
    formData.append('language', language);
    const response = await axios.post(
      `${API_BASE_URL}/cv/analyze`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } }
    );
    const { result } = response.data;
    if (!result || !Array.isArray(result.competencies)) {
      throw new Error('CV analysis returned an invalid payload.');
    }
    return result;
  },

  /**
   * Load CV analysis data directly from disk
   * @param {string} userId - The user ID to load data for
   * @returns {Promise<{ competencies: Array, userId: string }>}
   */
  loadCvData: async (userId) => {
    logger.info('loadCvData', `Loading CV data from disk for user: ${userId}`, { userId });
    logger.info('loadCvData', `API URL: ${API_BASE_URL}/cv/load/${userId}`, { url: `${API_BASE_URL}/cv/load/${userId}` });

    try {
      logger.info('loadCvData', 'Making API request to load CV data...', {});
      const response = await axios.get(
        `${API_BASE_URL}/cv/load/${userId}`
      );

      const { result } = response.data || {};
      const competencies = Array.isArray(result)
        ? result
        : Array.isArray(result?.competencies)
          ? result.competencies
          : [];
      logger.info('loadCvData', 'CV data loaded from disk', { dataSize: competencies.length });

      if (!result) {
        logger.info('loadCvData', 'No CV data found, returning empty competencies', {});
        return { competencies: [], userId };
      }

      logger.info('loadCvData', 'Returning CV data', { competencyCount: competencies.length });
      return { ...result, competencies, userId: result.userId || userId };
    } catch (error) {
      logger.error('loadCvData', 'Error loading CV data from disk', error, ErrorType.API_CALL);
      logger.error('loadCvData', 'Error details', { message: error.message }, ErrorType.API_CALL);
      if (error.response) {
        logger.error('loadCvData', 'Error response', { status: error.response.status, data: error.response.data }, ErrorType.API_CALL);
      }
      throw error;
    }
  },

  /**
   * Get CV categories from the backend (loaded from database)
   * @returns {Promise<Array<string>>} Array of category names
   */
  getCategories: async () => {
    const url = `${API_BASE_URL}/system/categories/names?category_type=cv&active_only=true`;
    logger.info('getCategories', 'Loading CV categories from backend database', { url });

    try {
      const response = await axios.get(url);
      const { result } = response.data;

      logger.info('getCategories', `Successfully loaded ${result?.length || 0} categories from database`, {
        url,
        status: response.status,
        categories: result
      });
      return result || [];
    } catch (error) {
      logger.error('getCategories', 'Error loading categories from backend', {
        url,
        error: error.message,
        status: error.response?.status,
        responseData: error.response?.data
      }, ErrorType.API_CALL);
      throw error;
    }
  }
};

export default CVAnalyzerService;
